'use client';

import { useEffect, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { useModalOverlayDismiss } from '@/hooks/useModalOverlayDismiss';
import { cn } from '@/components/ui/utils';

/** Точные классы оверлея как у прежних модалок (фон + flex-оболочка). */
export const modalOverlayClass = {
  participantEdit:
    'fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4',
  userProfileEdit:
    'fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-black/50 px-4 pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6',
  registrationInfo:
    'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in',
  participantSelect:
    'fixed inset-0 bg-black/60 backdrop-blur-md z-[100] flex items-center justify-center p-4',
  /** Artist bio on festival landing: sheet on small screens, centered from sm+. */
  artistBio:
    'fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-4 backdrop-blur-[2px] sm:items-center sm:p-6',
  success: 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4',
} as const;

export type ModalOverlayPreset = keyof typeof modalOverlayClass;

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  overlayPreset: ModalOverlayPreset;
  /** Доп. классы к оверлею (twMerge). */
  overlayClassName?: string;
  panelClassName: string;
  panelProps?: Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'children' | 'onMouseDown'>;
  children: ReactNode;
  /** Как в UserProfileEditModal: html/body overflow hidden. */
  lockBodyScroll?: boolean;
};

export function Modal({
  open,
  onClose,
  overlayPreset,
  overlayClassName,
  panelClassName,
  panelProps,
  children,
  lockBodyScroll = false,
}: ModalProps) {
  const { onBackdropPointerDown } = useModalOverlayDismiss(open, onClose);

  useEffect(() => {
    if (!open || !lockBodyScroll) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [open, lockBodyScroll]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      onMouseDown={onBackdropPointerDown}
      className={cn(modalOverlayClass[overlayPreset], overlayClassName)}
    >
      <div
        className={panelClassName}
        onMouseDown={(e) => e.stopPropagation()}
        {...panelProps}
      >
        {children}
      </div>
    </div>
  );
}
